"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Session = void 0;
const axios_1 = require("axios");
const utils_1 = require("./utils");
const loginKeys = ['required_authentication', 'session_code_expired'];
class Session {
    _httpInstance;
    _clientId = ''; // 客户端id
    _cookieDomain = ''; // cookie域名
    _handleLogin; // 跳转到账户中心的实现
    _accessToken = '';
    _tokenExpires = 0;
    _tokenAuthCode = '';
    /**
       * 该key值用来从cookie中判断是否已登录
       */
    _reoOnlineSign = 'REO_ONLINE_SIGN';
    _account;
    _issuedAt;
    _tokenCreateTime;
    /**
     * 用户登录后存入cookie的auth code的key值
     */
    _reoSessionAuthCode = 'REO_SESS_AUTH_CODE';
    _reoAccessToken = 'REO_ACCESS_TOKEN';
    _reoAccessExpires = 'REO_ACCESS_EXPIRES';
    // 当前token对应的auth code
    _reoCurrentTokenAuthCodeKey = 'REO_CURRENT_TOKEN_AUTH_CODE';
    _reoIssuedAt = 'REO_ISSUED_AT';
    _reoCurrentAccount = 'REO_CURRENT_ACCOUNT';
    _fetchTokenPromise;
    constructor(sessionOption) {
        const tokenKeySuffix = sessionOption.tokenKeySuffix?.trim();
        if (tokenKeySuffix) {
            this._reoAccessToken = this._reoAccessToken + `_${tokenKeySuffix}`;
            this._reoAccessExpires = this._reoAccessExpires + `_${tokenKeySuffix}`;
            this._reoCurrentTokenAuthCodeKey = this._reoCurrentTokenAuthCodeKey + `_${tokenKeySuffix}`;
        }
        this._handleLogin = sessionOption.handleLogin;
        this._cookieDomain = sessionOption.cookieDomain;
        this._clientId = sessionOption.clientId;
        this._httpInstance = axios_1.default.create({
            baseURL: sessionOption.baseUrl,
            withCredentials: true
        });
        this._httpInstance.interceptors.response.use(undefined, (err) => {
            const symbol = err?.response?.data?.error?.symbol;
            this.fetchErrorHandle(symbol);
            return Promise.reject(err);
        });
        this._accessToken = (0, utils_1.getClientData)(this._reoAccessToken);
        this._tokenExpires = parseInt((0, utils_1.getClientData)(this._reoAccessExpires));
        this._tokenAuthCode = (0, utils_1.getClientData)(this._reoCurrentTokenAuthCodeKey);
        this._tokenCreateTime = new Date().getTime();
    }
    /**
     * 检验用户是否已在账户中心登录
     */
    get isAccountCenterLogin() {
        const code = (0, utils_1.getCookie)(this._reoOnlineSign);
        return code !== '';
    }
    /**
     * 检验token是否过期
     */
    get _tokenValid() {
        const now = new Date().getTime() + 60000;
        return (this._accessToken !== '' &&
            this._tokenExpires > now &&
            // 当前token对应的auth code与session_auth_code一致, 防止账号变更、同账号反复登录token失效
            this._tokenAuthCode === (0, utils_1.getCookie)(this._reoSessionAuthCode));
    }
    get _sessionValid() {
        return this._account === Number((0, utils_1.getClientData)(this._reoCurrentAccount))
            && this._issuedAt === Number((0, utils_1.getClientData)(this._reoIssuedAt));
    }
    // 刷新token
    async refreshToken() {
        if (this.isAccountCenterLogin) {
            this._fetchTokenPromise = undefined;
            const tokenData = await this._fetchToken();
            const { accessToken, tokenExpires, tokenAuthCode } = tokenData;
            this._accessToken = accessToken;
            this._tokenExpires = tokenExpires;
            this._tokenAuthCode = tokenAuthCode;
        }
        else {
            await this.goLogin();
        }
    }
    // 刷新session
    async _refreshSession() {
        const sessionData = await this._fetchSession();
        const currentAccount = (0, utils_1.getClientData)(this._reoCurrentAccount);
        const { account, issuedAt } = sessionData;
        if (currentAccount !== `${account}`) {
            this.clearAccessToken();
        }
        if (account === -1 && issuedAt === -1) {
            await this.goLogin();
            return;
        }
        const expires = new Date(365 * 86400 * 1000 + new Date().getTime()).toUTCString();
        (0, utils_1.saveInClient)(this._reoCurrentAccount, `${account}`, expires);
        (0, utils_1.saveInClient)(this._reoIssuedAt, `${issuedAt}`, expires);
        this._account = sessionData.account;
        this._issuedAt = sessionData.issuedAt;
    }
    async _fetchToken() {
        if (!this._fetchTokenPromise) {
            this._fetchTokenPromise = this._getTokenAsyncFn();
        }
        const tokenData = await this._fetchTokenPromise;
        return tokenData;
    }
    /**
     * 获取当前登录用户的会话信息
     */
    async _fetchSession() {
        const sessionCode = (0, utils_1.getCookie)(this._reoSessionAuthCode);
        if (sessionCode) {
            const res = await this._httpInstance.get('/oauth2/session/details/', {
                withCredentials: true,
                headers: {
                    Authorization: `refresh_token ${sessionCode}`
                }
            });
            return {
                account: res.data.account,
                issuedAt: res.data.issuedAt
            };
        }
        return {
            account: -1,
            issuedAt: -1,
        };
    }
    async init() {
        if (!this.isAccountCenterLogin) {
            await this.goLogin();
        }
        await this._refreshSession();
        if (!(this._tokenValid && this._sessionValid)) {
            await this.refreshToken();
        }
    }
    async _destroySessionCode() {
        const code = (0, utils_1.getCookie)(this._reoSessionAuthCode);
        if (code) {
            try {
                await this._httpInstance.delete('/oauth2/token/', {
                    withCredentials: true,
                    headers: {
                        Authorization: `refresh_token ${code}`
                    }
                });
            }
            catch {
                return true; //无论是否成功清除session_code，均视为成功，当session_code失效时，上诉接口会失败
            }
        }
        return true;
    }
    /**
     * 前往账户中心登录
     * @param redirect 登录成功后的跳转地址
     */
    async goLogin() {
        const res = await this._destroySessionCode();
        const expires = new Date(365 * 86400 * 1000 + new Date().getTime()).toUTCString();
        this.clearLoginCookie();
        (0, utils_1.saveInClient)(this._reoAccessToken, '', expires);
        (0, utils_1.saveInClient)(this._reoAccessExpires, '', expires);
        (0, utils_1.saveInClient)(this._reoCurrentTokenAuthCodeKey, '', expires);
        (0, utils_1.saveInClient)(this._reoCurrentAccount, '', expires);
        (0, utils_1.saveInClient)(this._reoIssuedAt, '', expires);
        if (res) {
            this._handleLogin();
        }
    }
    clearLoginCookie() {
        (0, utils_1.setCookie)(this._reoOnlineSign, '', this._cookieDomain, -3600);
        (0, utils_1.setCookie)(this._reoSessionAuthCode, '', this._cookieDomain, -3600);
    }
    async getToken() {
        if (!this._tokenValid) {
            const tokenDate = await this._fetchToken();
            const { accessToken, tokenExpires, tokenAuthCode } = tokenDate;
            this._accessToken = accessToken;
            this._tokenExpires = tokenExpires;
            this._tokenAuthCode = tokenAuthCode;
        }
        return `Bearer ${this._accessToken}`;
    }
    async _getTokenAsyncFn() {
        const code = (0, utils_1.getCookie)(this._reoSessionAuthCode);
        if (code) {
            const body = {
                client_id: this._clientId,
                grant_type: 'session_implicit',
                code: code,
                scope: 'account_service.read_user_profile;'
            };
            this._tokenCreateTime = new Date().getTime();
            try {
                const res = await this._httpInstance.post('/oauth2/token/', (0, utils_1.formatToFormUrlencoded)(body), {
                    headers: {
                        'content-type': 'application/x-www-form-urlencoded'
                    },
                    withCredentials: true
                });
                const accessToken = res.data.access_token;
                const tokenExpires = res.data.expires_in * 1000 + new Date().getTime();
                const expires = new Date(365 * 86400 * 1000 + new Date().getTime()).toUTCString();
                (0, utils_1.saveInClient)(this._reoAccessToken, `${accessToken}`, expires);
                (0, utils_1.saveInClient)(this._reoAccessExpires, `${tokenExpires}`, expires);
                (0, utils_1.saveInClient)(this._reoCurrentTokenAuthCodeKey, `${code}`, expires);
                // 已完成token刷新，消费掉对应的fetchTokenPromise
                this._fetchTokenPromise = undefined;
                return {
                    accessToken,
                    tokenExpires,
                    tokenAuthCode: code
                };
            }
            catch (e) {
                // 已完成token刷新，消费掉对应的fetchTokenPromise
                this._fetchTokenPromise = undefined;
                throw e;
            }
        }
        else {
            await this.goLogin();
            return {
                accessToken: '',
                tokenExpires: -3600,
                tokenAuthCode: ''
            };
        }
    }
    getTokenCreateTime() {
        return this._tokenCreateTime;
    }
    /**
     * @deprecated token应该由session模块内部维护，如果需要刷新token，请使用refreshToken
     */
    setAccessToken(accessToken, tokenExpires) {
        const expiresAt = new Date(365 * 86400 * 1000 + new Date().getTime()).toUTCString();
        (0, utils_1.saveInClient)(this._reoAccessToken, `${accessToken}`, expiresAt);
        (0, utils_1.saveInClient)(this._reoAccessExpires, `${tokenExpires}`, expiresAt);
        this._accessToken = accessToken;
        this._tokenExpires = tokenExpires;
    }
    clearAccessToken() {
        const expiresAt = new Date(365 * 86400 * 1000 + new Date().getTime()).toUTCString();
        (0, utils_1.saveInClient)(this._reoAccessToken, '', expiresAt);
        (0, utils_1.saveInClient)(this._reoAccessExpires, '', expiresAt);
        (0, utils_1.saveInClient)(this._reoCurrentTokenAuthCodeKey, '', expiresAt);
        (0, utils_1.saveInClient)(this._reoCurrentAccount, '', expiresAt);
        (0, utils_1.saveInClient)(this._reoIssuedAt, '', expiresAt);
        this._accessToken = '';
        this._tokenExpires = 0;
        this._account = 0;
        this._issuedAt = 0;
    }
    fetchErrorHandle(symbol) {
        if (symbol && loginKeys.includes(symbol)) {
            // eslint-disable-next-line @reolink/general/disable-promise-then-catch
            this.goLogin().catch(console.error);
        }
    }
}
exports.Session = Session;
//# sourceMappingURL=Session.js.map